import { useContext, useEffect, useState } from "react";
import { useSetState } from "ahooks";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Col, Row, Select, Space, Spin } from "antd";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { ProjectCard } from "../../components/ProjectCard";
import styles from "./Portfolio.module.scss";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../components/Routing/ApiContext";

export const Portfolio: React.FC = () => {
  const api = useContext(ApiContext)?.api; //get URL to backend API via ApiContext/ApiProvider mechanism
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterType, setFilterType] = useState<string[]>();
  const [filterStack, setFilterStack] = useState<string[]>();
  const [filters, setFilters] = useSetState({
    types: [],
    stack: [],
  });

  const fetchProjects = async (
    filters = {} as { type?: Array<string>; stack?: Array<string> }
  ) => {
    setLoading(true);
    try {
      const response = await axios.get(`${api}/projects`, {
        params: {
          type: filters.type || searchParams.getAll("type"),
          stack: filters.stack || searchParams.getAll("stack"),
        },
        withCredentials: true,
      });
      const projects = response.data;
      setProjects(projects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFilters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${api}/filters`, {
        withCredentials: true,
      });
      setFilters({
        types: response.data.types,
        stack: response.data.stack,
      });
    } catch (error) {
      console.error("Error fetching filters:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllFilters();
    setFilterType(searchParams.getAll("type") as string[]);
    setFilterStack(searchParams.getAll("stack") as string[]);
  }, [searchParams]);

  useEffect(() => {
    fetchProjects({ type: filterType, stack: filterStack });
  }, [filterType, filterStack]);

  const typeFilterOptions = filters.types?.map((item) => ({
    value: item,
    label: item,
  }));

  const stackFilterOptions = filters.stack?.map((item) => ({
    value: item,
    label: item,
  }));

  function handleChangeType(values: string[]) {
    setFilterType(values);
    setSearchParams((prevValues) => {
      return { stack: prevValues.getAll("stack"), type: values };
    });
  }

  function handleChangeStack(values: string[]) {
    setFilterStack(values);
    setSearchParams((prevValues) => {
      return { type: prevValues.getAll("type"), stack: values };
    });
  }

  return (
    <Spin spinning={loading}>
      <div className={styles.wrapper}>
        <div className={styles.wrapperChild}>
          <Space direction="vertical" size="middle">
            <div className={styles.filters}>
              <span className={styles.filterTitle}>
                {t("portfolio.filters")}
              </span>
              <div className={styles.filtersColumn}>
                <Select
                  style={{ minWidth: 200, width: "100%" }}
                  placeholder={t("portfolio.type")}
                  mode="tags"
                  allowClear
                  value={filterType}
                  onChange={handleChangeType}
                  options={_.unionBy(typeFilterOptions, (item) => item.value)}
                />
              </div>
              <div className={styles.filtersColumn}>
                <Select
                  style={{ minWidth: 150, width: "100%" }}
                  placeholder={t("portfolio.stack")}
                  mode="tags"
                  allowClear
                  value={filterStack}
                  onChange={handleChangeStack}
                  options={_.unionBy(stackFilterOptions, (item) => item.value)}
                />
              </div>
            </div>
            <Row gutter={[32, 24]}>
              {projects.length > 0 ? (
                projects.map((project) => (
                  <Col xs={24} md={12} xl={8} key={project._id}>
                    <ProjectCard project={project} />
                  </Col>
                ))
              ) : (
                <div className={styles.noProjectsBlock}>
                  <div>{t("portfolio.noProjects")}</div>
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    size="5x"
                    style={{ color: "#c0c0c0", marginTop: 25 }}
                  />
                </div>
              )}
            </Row>
          </Space>
        </div>
      </div>
    </Spin>
  );
}
